import { useState, useCallback } from 'react';

export type SnackbarSeverity = 'success' | 'error' | 'warning' | 'info';

interface UseSnackbarReturn {
  open: boolean;
  message: string;
  severity: SnackbarSeverity;
  /** Retry action for the current error toast (SnackbarComponent `onRetry`). */
  retry: (() => void) | undefined;
  showSuccess: (message: string) => void;
  showError: (message: string, retry?: () => void) => void;
  showWarning: (message: string) => void;
  showInfo: (message: string) => void;
  show: (
    message: string,
    severity: SnackbarSeverity,
    retry?: () => void
  ) => void;
  close: () => void;
}

/**
 * Hook to manage snackbar state and provide convenience methods for showing messages.
 * Consolidates snackbar state management to reduce boilerplate.
 *
 * @returns Object containing snackbar state and methods to control it
 *
 * @example
 * ```tsx
 * const { open, message, severity, showSuccess, showError, close } = useSnackbar();
 *
 * // Show success message
 * showSuccess('Operation completed successfully');
 *
 * // Show error message (optionally with a retry action)
 * showError('Something went wrong', refetch);
 *
 * // In JSX
 * <SnackbarComponent
 *   open={open}
 *   message={message}
 *   severity={severity}
 *   onClose={close}
 *   onRetry={retry}
 * />
 * ```
 */
export const useSnackbar = (): UseSnackbarReturn => {
  const [open, setOpen] = useState<boolean>(false);
  const [message, setMessage] = useState<string>('');
  const [severity, setSeverity] = useState<SnackbarSeverity>('success');
  const [retry, setRetry] = useState<(() => void) | undefined>(undefined);

  const show = useCallback(
    (
      newMessage: string,
      newSeverity: SnackbarSeverity,
      newRetry?: () => void
    ) => {
      setMessage(newMessage);
      setSeverity(newSeverity);
      setRetry(() => newRetry);
      setOpen(true);
    },
    []
  );

  const showSuccess = useCallback(
    (newMessage: string) => {
      show(newMessage, 'success');
    },
    [show]
  );

  const showError = useCallback(
    (newMessage: string, newRetry?: () => void) => {
      show(newMessage, 'error', newRetry);
    },
    [show]
  );

  const showWarning = useCallback(
    (newMessage: string) => {
      show(newMessage, 'warning');
    },
    [show]
  );

  const showInfo = useCallback(
    (newMessage: string) => {
      show(newMessage, 'info');
    },
    [show]
  );

  const close = useCallback(() => {
    setOpen(false);
  }, []);

  return {
    open,
    message,
    severity,
    retry,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    show,
    close,
  };
};
